import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@mono/ui-components';
import { ChevronRight, FolderOpen, Home, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { documentsApi } from '../../../lib/api/documentsApi.connect';
/**
 * Lazy folder tree navigator — shows the children of whatever folder the user
 * has currently drilled into. "Root" is a virtual parent with empty id.
 * Keeps state local; no recursive full-tree load (fine for now, we expect
 * dozens of folders, not thousands).
 */ export function MoveDialog({ open, onOpenChange, documentName, currentFolderId, onMove }) {
    const [stack, setStack] = useState([]);
    const [children, setChildren] = useState([]);
    const [loading, setLoading] = useState(false);
    const [moving, setMoving] = useState(false);
    const activeId = stack.at(-1)?.id;
    // Load children when stack changes.
    useEffect(()=>{
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        documentsApi.listFolders(activeId).then((f)=>{
            if (!cancelled) setChildren(f);
        }).finally(()=>{
            if (!cancelled) setLoading(false);
        });
        return ()=>{
            cancelled = true;
        };
    }, [
        activeId,
        open
    ]);
    // Reset to root each time the dialog opens.
    useEffect(()=>{
        if (open) setStack([]);
    }, [
        open
    ]);
    async function handleMove() {
        setMoving(true);
        try {
            await onMove(activeId);
            onOpenChange(false);
        } finally{
            setMoving(false);
        }
    }
    const isCurrent = (activeId ?? undefined) === (currentFolderId ?? undefined);
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            className: "sm:max-w-md",
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            children: "Move document"
                        }),
                        /*#__PURE__*/ _jsxs(DialogDescription, {
                            children: [
                                "Pick a destination folder for “",
                                documentName,
                                "”."
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-1 text-sm flex-wrap mb-2",
                    children: [
                        /*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            onClick: ()=>setStack([]),
                            className: "inline-flex items-center gap-1 text-muted-foreground hover:text-foreground",
                            children: [
                                /*#__PURE__*/ _jsx(Home, {
                                    className: "w-4 h-4"
                                }),
                                "Root"
                            ]
                        }),
                        stack.map((f, i)=>/*#__PURE__*/ _jsxs("span", {
                                className: "flex items-center gap-1",
                                children: [
                                    /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: "w-4 h-4 text-muted-foreground"
                                    }),
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>setStack(stack.slice(0, i + 1)),
                                        className: i === stack.length - 1 ? 'font-medium' : 'text-muted-foreground hover:text-foreground',
                                        children: f.name
                                    })
                                ]
                            }, f.id))
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "max-h-64 overflow-y-auto rounded border divide-y",
                    children: loading ? /*#__PURE__*/ _jsx("div", {
                        className: "flex items-center justify-center py-8",
                        children: /*#__PURE__*/ _jsx(Loader2, {
                            className: "w-5 h-5 animate-spin text-muted-foreground"
                        })
                    }) : children.length === 0 ? /*#__PURE__*/ _jsx("p", {
                        className: "px-3 py-4 text-sm text-muted-foreground",
                        children: "No subfolders here. Move into this one or go back."
                    }) : children.map((f)=>/*#__PURE__*/ _jsxs("button", {
                            type: "button",
                            onClick: ()=>setStack([
                                    ...stack,
                                    f
                                ]),
                            className: "w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted",
                            children: [
                                /*#__PURE__*/ _jsx(FolderOpen, {
                                    className: "w-4 h-4 text-muted-foreground"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "flex-1 truncate",
                                    children: f.name
                                }),
                                /*#__PURE__*/ _jsx(ChevronRight, {
                                    className: "w-4 h-4 text-muted-foreground"
                                })
                            ]
                        }, f.id))
                }),
                /*#__PURE__*/ _jsxs(DialogFooter, {
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            onClick: ()=>onOpenChange(false),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            onClick: handleMove,
                            disabled: moving || isCurrent,
                            children: moving ? 'Moving…' : isCurrent ? 'Already here' : `Move to ${stack.at(-1)?.name ?? 'Root'}`
                        })
                    ]
                })
            ]
        })
    });
}
